import styled, { css, keyframes } from 'styled-components';
import { assignIfDefined, opaque } from 'styles/Utils';

/* Mixins */
import { FlexCenterChildren, WebFontBody1, WebFontBody2 } from 'styles/Mixins';

const TRANSITION_TIME = '0.3s';

export const SelectBox = styled.div<{ [props: string]: any }>`
  margin: ${({ margin }) => margin || '0 4px'};
  box-sizing: border-box;
  ${assignIfDefined('width')};
  ${assignIfDefined('height')};
  color: ${({ theme }) => theme.Grey1};
  border-radius: 2px;
  border: solid 1px
    ${({ theme, isError, isWarning }) => {
      if (isError) {
        return theme.wishRed;
      } else if (isWarning) {
        return theme.wishDarkYellow;
      } else {
        return theme.Grey5;
      }
    }};
  background-color: ${({ theme, isWarning }) =>
    isWarning ? opaque(theme.wishLightYellow, 0.1) : theme.Grey7};
  position: relative;
  ${FlexCenterChildren};
  ${({ disabled }) =>
    disabled
      ? 'cursor: not-allowed; opacity: 0.5; pointer-events:none;'
      : 'cursor: pointer;'};
`;

const ItemHeightMixins = ({
  size,
}: {
  size: 'extra-small' | 'small' | 'splash' | 'mobile';
}) => {
  switch (size) {
    case 'extra-small':
      return 24;
    case 'small':
      return 32;
    case 'mobile':
      return 48;
    default:
      return 42;
  }
};

const BoxTextMixins = ({ size }: any) => {
  const itemHeight = ItemHeightMixins({ size });
  switch (size) {
    case 'small':
    case 'extra-small':
    case 'mobile':
      return `${WebFontBody2}; min-height:${itemHeight}px;`;
    case 'splash':
      return `${WebFontBody1}; min-height:${itemHeight}px;`;
    default:
      return `${WebFontBody1}; min-height:${itemHeight}px;`;
  }
};

// ie bug with align-items: center and min-height requires the :after
// https://github.com/philipwalton/flexbugs/issues/231
export const SelectBoxText = styled.div<{ [props: string]: any }>`
  padding: ${({ boxPadding }) => boxPadding || '0 0 0 16px'};
  text-align: left;
  ${BoxTextMixins};
  width: calc(100% - 32px);
  display: flex;
  align-items: center;
  :after {
    display: block;
    content: '';
    min-height: inherit;
    font-size: 0;
  }
`;

export const DropdownArrow = styled.div<{ [props: string]: any }>`
  display: flex;
  margin: 0 12px;
  width: 8px;
  transform: rotateZ(${({ visible }) => (visible ? -180 : 0)}deg);
  transition: all ${TRANSITION_TIME};
`;

const borderKeyFrame = keyframes`
  0% {
    border-width: 0px;
  }

  5% {
    border-width: 1px;
  }

  100% {
    border-width: 1px;
  }
`;

const borderAnimationRule = css`
  ${borderKeyFrame} ${TRANSITION_TIME} both;
`;

const borderKeyFrameReversed = keyframes`
  0% {
    border-width: 1px;
  }

  95% {
    border-width: 1px;
  }

  100% {
    border-width: 0px;
  }
`;

const borderReversedAnimationRule = css`
  ${borderKeyFrameReversed} ${TRANSITION_TIME} both;
`;

export const SelectBoxDropdown = styled.div<{ [props: string]: any }>`
  box-sizing: border-box;
  position: absolute;
  height: ${({ isVisible, dropdownHeight, size, maxItems }) =>
    isVisible
      ? `${Math.min(maxItems * ItemHeightMixins({ size }), dropdownHeight)}px`
      : '0'};
  overflow-y: hidden;
  ${({ itemLength, maxItems }) =>
    itemLength > maxItems && 'overflow-y: scroll'};
  ${({ isOpenDown, selectBoxHeight }) =>
    isOpenDown
      ? `top: ${selectBoxHeight - 2}px`
      : `bottom: ${selectBoxHeight - 2}px`};
  left: 0;
  background-color: ${({ theme }) => theme.white};
  border-radius: 2px;
  border-style: solid;
  border-width: 0;
  border-color: ${({ theme }) => theme.Grey5};
  transition: height ${TRANSITION_TIME};
  animation: ${({ isVisible }) =>
    isVisible ? borderAnimationRule : borderReversedAnimationRule};
  z-index: ${({ theme }) => theme.zIndexBase2};
  ${({ isExpandable }) => (isExpandable ? 'min-width: 100%' : 'width: 100%')};
  ${({ noWrap }) => noWrap && 'white-space: nowrap'};
`;

export const SelectBoxDropdownItem = styled.div<{ [props: string]: any }>`
  ${BoxTextMixins};
  ${FlexCenterChildren};
  ${assignIfDefined('padding')};
  ${assignIfDefined('justifyContent', 'justify-content')};
  flex-shrink: 0;
  :hover {
    background-color: ${({ theme }) => theme.Grey7};
  }
`;

export const MobileDropdownItem = styled(SelectBoxDropdownItem)`
  ${WebFontBody1};
`;
